import React from 'react'
import type { LucideIcon } from 'lucide-react'
import { cn } from '@/lib/utils'
import { imageSrcSet, imageUrl, type ImageOptions } from '@/lib/images'

/**
 * Soft diagonal wash built from the community palette (primary + secondary),
 * with two faint glows so it reads as a deliberate surface, not an empty box.
 * Everything comes from the runtime CSS variables, so it follows any theme.
 */
const PLACEHOLDER_BACKGROUND = [
  'radial-gradient(circle at 88% 12%, color-mix(in srgb, var(--color-secondary-300) 55%, transparent) 0%, transparent 55%)',
  'radial-gradient(circle at 8% 92%, color-mix(in srgb, var(--color-primary-400) 30%, transparent) 0%, transparent 60%)',
  'linear-gradient(135deg, var(--color-primary-100) 0%, var(--color-primary-50) 48%, var(--color-secondary-100) 100%)',
].join(', ')

const ICON_COLOR = 'color-mix(in srgb, var(--color-primary-700) 35%, transparent)'

interface ImagePlaceholderProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Optional discreet icon of the module, centered. */
  icon?: LucideIcon
  iconClassName?: string
}

/** Stand-in for an optional image that is missing: fills its slot with a community-colored gradient. */
export const ImagePlaceholder = ({ icon: Icon, iconClassName, className, style, ...rest }: ImagePlaceholderProps) => (
  <div
    aria-hidden={rest['aria-label'] ? undefined : true}
    {...rest}
    data-image-placeholder=""
    className={cn('flex items-center justify-center', className)}
    style={{ backgroundImage: PLACEHOLDER_BACKGROUND, ...style }}
  >
    {Icon && <Icon className={cn('h-8 w-8', iconClassName)} style={{ color: ICON_COLOR }} strokeWidth={1.5} />}
  </div>
)

interface CoverImageProps extends Omit<React.ImgHTMLAttributes<HTMLImageElement>, 'src'> {
  src?: string | null
  icon?: LucideIcon
  iconClassName?: string
  /** Extra classes for the placeholder only; `className` goes to both. */
  placeholderClassName?: string
  /**
   * How a platform image is asked of imgix: CSS width of the slot (2x comes from `srcSet`).
   * Defaults to a card-sized 960px; pass the real width where the slot is much smaller or larger.
   * A `src` that is not ours, or already rewritten by the caller, is drawn as given.
   */
  cdn?: ImageOptions & { w: number }
}

const DEFAULT_CDN = { w: 960, fit: 'max' } as const

/**
 * An optional image: the picture when there is one, the gradient placeholder
 * when there is none or it fails to load. Give it the slot's size in `className`
 * (usually `h-full w-full`) so nothing shifts between the two.
 */
export const CoverImage = ({ src, icon, iconClassName, placeholderClassName, className, alt = '', onError, cdn = DEFAULT_CDN, srcSet, ...rest }: CoverImageProps) => {
  /* Every URL that failed to load for this `src`. A new `src` starts clean. */
  const [failed, setFailed] = React.useState<{ src: string | null | undefined; urls: string[] }>({ src, urls: [] })
  const failedUrls = failed.src === src ? failed.urls : []
  const resized = imageUrl(src, cdn) || null
  const proxied = !!resized && resized !== src
  /* If the proxy fails, the original file is the fallback before the placeholder. */
  const shown = [resized, src].find((url): url is string => !!url && !failedUrls.includes(url))

  if (!shown) {
    return <ImagePlaceholder icon={icon} iconClassName={iconClassName} className={cn(className, placeholderClassName)} onClick={rest.onClick as React.MouseEventHandler<HTMLDivElement> | undefined} />
  }
  const fallingBack = proxied && shown === src
  return (
    <img
      {...rest}
      src={shown}
      srcSet={fallingBack ? undefined : srcSet ?? (proxied ? imageSrcSet(src, cdn) : undefined)}
      alt={alt}
      className={className}
      onError={(e) => { setFailed({ src, urls: [...failedUrls, shown] }); onError?.(e) }}
    />
  )
}
